import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Avatar, Button, InlineError, Skeleton } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { CommentEditor } from '../comments/CommentEditor';
import { CommentBody } from '../comments/CommentBody';
import { CommentReactions } from '../comments/CommentReactions';
import { formatCommentTime } from '../comments/CommentsSection';
import { useCreateComment } from '../comments/hooks';
import { commentErrorMessage } from '../comments/queries';
import { personMentionsIn } from '../comments/commentHtml';
import { useCommentReactions, useToggleReaction } from '../comments/reactionHooks';
import { mayReact, reactionErrorMessage } from '../comments/reactions';
import { usePasteImageUpload } from '../comments/pasteImage';
import { isRichTextEmpty } from '../../components/RichText';
import type { MentionPerson } from '../comments/commentMentions';
import type { MentionSources } from '../notes/noteMentions';
import {
  useHuddleChat,
  useHuddleChatUnread,
  useMarkHuddleChatRead,
  useRefreshHuddleChat,
  useRoomMembers,
} from './hooks';
import { groupHuddleThreads, huddleTaskHref } from './huddleChat';
import type { HuddleThread } from './huddleChat';
import {
  membersOnlyPeople,
  mentionNotDeliveredMessage,
  mentionRefusedMessage,
} from './mentionPeople';

/**
 * The "Alex Huddle" chat in Messages (KYU_DECISION 25 Sept 20:10;
 * plan-drop3b.md 3b-2 PAGE PARTS).
 *
 * What it shows: the comments Alex leaves through the huddle link, on the
 * tasks of the signed-in person's live huddle section, one thread per task,
 * each linking to the task. The rows come from my_huddle_chat
 * (20270181000100), which copies nothing and answers only the link's owner.
 *
 * Replying: a reply is an ordinary task comment on that task, through the
 * same useCreateComment path every task thread uses, so it lands in the task's
 * comments where Alex already sees it. The chat itself lists Alex's side
 * only; the reply shows on the task.
 *
 * Mentions (3b-1 Q11, the drop after 3b-2): an @ person in a reply is a real
 * mention, read out of the reply's own markup exactly as the Discussion tab
 * and the Messages composer do. The picker offers the people in the task's
 * project (project_members, the set the Discussion tab calls "in this
 * project"). The thread's project is always the signed-in person's own
 * Personal project (my_huddle_chat, 20270181000100), so in practice that is
 * the owner alone unless they added someone. A draft naming anyone else is
 * not sent and stays; a refusal after posting clears the draft and says
 * nobody was notified.
 *
 * Read state: mark_huddle_chat_read when the chat is opened and again when a
 * newer comment arrives while it is open (same pattern as a room).
 */

function ThreadReply({
  thread,
  sources,
  people,
  onDone,
}: {
  thread: HuddleThread;
  sources: MentionSources;
  /** Who the @ picker offers: the project's members, active TAP only. */
  people: MentionPerson[];
  onDone: () => void;
}) {
  const { client } = useAuth();
  const createComment = useCreateComment(thread.taskId, thread.projectId);
  const pasteImage = usePasteImageUpload();
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  // A pasted screenshot still uploading: Reply waits for it (design note S16).
  const [uploading, setUploading] = useState(false);

  function send() {
    if (uploading) {
      return;
    }
    if (isRichTextEmpty(body) && !body.includes('<img')) {
      setError('Write a reply first.');
      return;
    }
    // The same two layers as the Messages composer (MessagesPage submit).
    const mentioned = personMentionsIn(body);
    const allowed = new Set(people.map((person) => person.id));
    const refused = mentioned.filter((person) => !allowed.has(person.id));
    if (refused.length > 0) {
      setError(mentionRefusedMessage('project', refused));
      return;
    }
    setError('');
    createComment.mutate(
      { body, mentionedUserIds: mentioned.map((person) => person.id) },
      {
        onSuccess: (result) => {
          setBody('');
          // Posted, but the mentions were refused (someone was deactivated
          // since the page loaded). Keep the box open so the sentence is
          // seen; the draft is gone so a second Reply cannot post it twice.
          if (result.mentionError !== null) {
            setError(mentionNotDeliveredMessage(result.mentionError));
            return;
          }
          onDone();
        },
        onError: (cause) =>
          setError(commentErrorMessage(cause instanceof Error ? cause.message : '')),
      },
    );
  }

  return (
    <div className="huddle-chat-reply">
      {error === '' ? null : <InlineError>{error}</InlineError>}
      <CommentEditor
        label={`Reply on ${thread.taskTitle}`}
        value={body}
        onChange={setBody}
        onSubmit={send}
        client={client}
        sources={sources}
        people={people}
        onPasteImage={pasteImage}
        onUploadingChange={setUploading}
      />
      <div className="huddle-chat-reply-actions">
        <span className="huddle-chat-reply-note">
          Your reply goes on the task, where Alex sees it.
        </span>
        <Button variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button size="sm" onClick={send} disabled={createComment.isPending || uploading}>
          {createComment.isPending ? 'Sending…' : 'Reply'}
        </Button>
      </div>
    </div>
  );
}

export function HuddleChatView({
  readOnly,
  sources,
  people,
}: {
  readOnly: boolean;
  sources: MentionSources;
  people: MentionPerson[];
}) {
  const { access } = useAuth();
  const chat = useHuddleChat(true);
  const unread = useHuddleChatUnread();
  const markRead = useMarkHuddleChatRead();
  const refresh = useRefreshHuddleChat();
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState('');

  const threads = useMemo(() => groupHuddleThreads(chat.data ?? []), [chat.data]);
  // The @ picker for a reply offers the members of that task's project, the
  // same people the Discussion tab counts as "in this project"
  // (CommentsSection outsiders note). One read for every thread's project.
  const projectIds = useMemo(
    () => [...new Set(threads.map((thread) => thread.projectId))].sort(),
    [threads],
  );
  const projectMembers = useRoomMembers(projectIds);
  const commentIds = useMemo(() => (chat.data ?? []).map((m) => m.commentId), [chat.data]);
  const reactions = useCommentReactions(commentIds);
  const toggleReaction = useToggleReaction();
  const canReact = mayReact(access.status === 'approved' ? access.profile : null);

  // Mark read on open, and again when a newer comment shows up while open.
  const newest =
    (chat.data ?? []).reduce<{ id: string; at: string } | null>(
      (best, m) =>
        best === null || m.createdAt > best.at ? { id: m.commentId, at: m.createdAt } : best,
      null,
    )?.id ?? '';
  const markedKey = useRef<string | null>(null);
  const { mutate: markHuddleRead } = markRead;
  useEffect(() => {
    if (chat.isPending || chat.isError) {
      return;
    }
    if (markedKey.current === newest) {
      return;
    }
    markedKey.current = newest;
    markHuddleRead();
  }, [chat.isPending, chat.isError, newest, markHuddleRead]);

  // The 60s unread poll notices a new Alex comment; reload the open chat.
  const pendingUnread = unread.data?.unread ?? 0;
  useEffect(() => {
    if (pendingUnread > 0) {
      refresh();
    }
  }, [pendingUnread, refresh]);

  function toggle(commentId: string, emoji: string, mine: boolean) {
    setError('');
    toggleReaction.mutate(
      { commentId, emoji, mine },
      {
        onError: (cause) => {
          const message = reactionErrorMessage(cause instanceof Error ? cause.message : '');
          if (message !== '') {
            setError(message);
          }
        },
      },
    );
  }

  return (
    <>
      <header className="messages-thread-header">
        <h2>Alex Huddle</h2>
        <p>Alex&apos;s comments from your huddle link, one thread per task.</p>
      </header>
      <div className="messages-thread-body">
        {error === '' ? null : <InlineError>{error}</InlineError>}
        {chat.isPending ? (
          <Skeleton lines={3} label="Loading the Alex Huddle chat" />
        ) : chat.isError ? (
          <p className="task-panel-text">The Alex Huddle chat could not be loaded.</p>
        ) : threads.length === 0 ? (
          <p className="task-panel-text">No comments from Alex yet</p>
        ) : (
          <ul className="huddle-chat-threads">
            {threads.map((thread) => (
              <li key={thread.taskId} className="huddle-chat-thread">
                <div className="huddle-chat-thread-head">
                  <Link className="huddle-chat-task-link" to={huddleTaskHref(thread)}>
                    {thread.taskTitle}
                  </Link>
                  <span className="huddle-chat-thread-count">
                    {thread.messages.length === 1
                      ? '1 comment'
                      : `${thread.messages.length} comments`}
                  </span>
                </div>
                <ul className="message-list huddle-chat-messages">
                  {[...thread.messages].reverse().map((message) => (
                    <li key={message.commentId} className="message-row">
                      <Avatar
                        name={message.authorName === '' ? 'Alex' : message.authorName}
                        imageUrl={message.authorAvatarUrl}
                        size="sm"
                      />
                      <div className="message-row-body">
                        <div className="message-row-meta">
                          <span className="message-row-author">{message.authorName || 'Alex'}</span>
                          <span className="message-row-time">
                            {formatCommentTime(message.createdAt)}
                            {message.editedAt === null ? '' : ' (edited)'}
                          </span>
                        </div>
                        <CommentBody html={message.body} />
                        <CommentReactions
                          reactions={reactions.data?.get(message.commentId)}
                          canReact={canReact}
                          pending={toggleReaction.isPending}
                          onToggle={(emoji, mine) => toggle(message.commentId, emoji, mine)}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
                {readOnly ? null : replyingTo === thread.taskId ? (
                  <ThreadReply
                    thread={thread}
                    sources={sources}
                    people={membersOnlyPeople(projectMembers.data?.get(thread.projectId), people)}
                    onDone={() => {
                      setReplyingTo(null);
                      setSentTo(thread.taskId);
                    }}
                  />
                ) : (
                  <div className="huddle-chat-thread-actions">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSentTo(null);
                        setReplyingTo(thread.taskId);
                      }}
                    >
                      Reply
                    </Button>
                    {sentTo === thread.taskId ? (
                      <span className="huddle-chat-reply-note">
                        Sent. It is on the task, <Link to={huddleTaskHref(thread)}>open it</Link>.
                      </span>
                    ) : null}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
